// ============================================================
// downscaleImage — shrink + recompress a photo in the browser before upload.
//
// Restaurant owners upload straight from their phones (4–8 MB, 4000px+). We
// cap the long edge and re-encode so uploads are fast, the live site stays
// light, and camera EXIF (including GPS location) is dropped — the canvas
// re-encode strips it. PNG/WebP keep their format (transparency-safe); every-
// thing else becomes JPEG. If anything is unsupported, the original is returned
// untouched so upload still works.
// ============================================================
const MAX_DIM = 2000;
const QUALITY = 0.82;

export async function downscaleImage(file: File): Promise<Blob> {
  if (!file.type.startsWith('image/')) return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
  } catch {
    return file; // decode/createImageBitmap unsupported → upload as-is
  }

  const { width, height } = bitmap;
  const scale = Math.min(1, MAX_DIM / Math.max(width, height));

  // Already within bounds → no re-encode needed; keep the original bytes.
  if (scale === 1) {
    bitmap.close?.();
    return file;
  }

  const w = Math.round(width * scale);
  const h = Math.round(height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close?.();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();

  const outType =
    file.type === 'image/png' ? 'image/png' : file.type === 'image/webp' ? 'image/webp' : 'image/jpeg';
  const quality = outType === 'image/png' ? undefined : QUALITY;

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, outType, quality));
  return blob ?? file;
}
